import {connect} from 'react-redux';

import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {findParentTags} from '../../../../../../helpers/models/tagHelpers';
import {TagModel} from '../../../../../../models/api/tagModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../services/tracking';
import {getHasPermission} from '../../../../../../store/data/access/accessSelectors';
import {
  bulkRemoveTagFromConversationsAction,
  removeTagFromConversationsAction,
} from '../../../../../../store/data/conversations/tagConversationsActions';
import {getTags} from '../../../../../../store/data/tags/tagsSelectors';
import {
  getFlatActiveOrPendingTeammates,
  getUserId,
} from '../../../../../../store/data/teammates/teammatesSelectors';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {InboxesVisualConversationSelection} from '../../../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {makeGetIsBulkActionRequired} from '../../../../../../store/panels/viewer/conversations/viewerPanelConversationsSelectors';
import {computeIsBulkActionRequired} from '../../../../../../store/panels/viewer/viewerPanelHelpers';
import {ConversationHeaderTagPill} from './conversationHeaderTagPill';

/*
 * Props.
 */

interface ConversationHeaderTagPillContainerProps {
  tag: TagModel;
  inboxesParameters: InboxesParameters;
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  conversationSelection: InboxesVisualConversationSelection;
}

/*
 * Selectors.
 */

function getTag(_state: unknown, props: ConversationHeaderTagPillContainerProps) {
  return props.tag;
}

function makeGetTagsPath() {
  return createSelector([getTags, getTag], (tags, tag) => [tag, ...findParentTags(tags, tag)]);
}

function getInboxesParameters(_state: unknown, props: ConversationHeaderTagPillContainerProps) {
  return props.inboxesParameters;
}

function getConversationSelection(_state: unknown, props: ConversationHeaderTagPillContainerProps) {
  return props.conversationSelection;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationHeaderTagPillContainerProps>()(() => ({
  userId: getUserId,
  tagsPath: wrapInShallowSelector(makeGetTagsPath()),
  teammates: getFlatActiveOrPendingTeammates,
  hasPermission: getHasPermission,
  isBulkActionRequired: makeGetIsBulkActionRequired(getInboxesParameters, getConversationSelection),
}));

const mapDispatchToProps = makeMapDispatchToProps<ConversationHeaderTagPillContainerProps>()((props) => ({
  onUntag: () => {
    const {tag, inboxesParameters, conversationSelection} = props;

    if (computeIsBulkActionRequired(inboxesParameters, conversationSelection)) {
      return bulkRemoveTagFromConversationsAction(inboxesParameters, props.cellsContext, tag.id);
    }

    const conversationIds = conversationSelection.knownIds;

    trackInterfaceEvent(InterfaceEventsEnum.P3_REMOVE_TAG_USING_HEADER, {
      conversation_ids: conversationIds,
      tag_id: tag.id,
    });

    return removeTagFromConversationsAction(conversationIds, tag.id);
  },
}));

export const ConversationHeaderTagPillContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ConversationHeaderTagPill);
